/* ── Overlay ────────────────────────────────────────────────── */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(8, 12, 18, 0.88);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-6);
}

/* ── Wizard card ────────────────────────────────────────────── */
.wizard {
  width: 100%;
  max-width: 600px;
  background: var(--color-surface);
  border: 1px solid var(--color-border-med);
  border-radius: var(--radius-xl, 16px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 32px 80px rgba(0,0,0,0.5);
  animation: fadeUp 0.25s ease;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Step indicator ─────────────────────────────────────────── */
.steps {
  display: flex;
  align-items: center;
  gap: 0;
  padding: var(--sp-6) var(--sp-8);
  border-bottom: 1px solid var(--color-border);
  position: relative;
}

.stepDot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  z-index: 1;
  flex-shrink: 0;
}

.stepDot + .stepDot {
  margin-left: auto;
}

.stepDotActive {
  background: rgba(56, 189, 248, 0.15);
  border-color: rgba(56, 189, 248, 0.4);
  color: var(--color-sky);
}

.stepDotDone {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.3);
  color: var(--color-success);
}

.stepLine {
  position: absolute;
  top: calc(50% + 1px);
  left: var(--sp-8);
  height: 1px;
  background: linear-gradient(90deg, rgba(56, 189, 248, 0.3), transparent);
  transition: width 0.4s ease;
  pointer-events: none;
}

/* ── Body ───────────────────────────────────────────────────── */
.body {
  padding: var(--sp-8);
  overflow-y: auto;
  max-height: calc(100vh - 240px);
}

/* ── Step ───────────────────────────────────────────────────── */
.step {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateX(8px); }
  to   { opacity: 1; transform: translateX(0); }
}

.stepHeader { display: flex; flex-direction: column; gap: var(--sp-2); }

.stepTag {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 2px;
  color: var(--color-sky);
  text-transform: uppercase;
}

.stepTitle {
  font-family: var(--font-mono);
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
}

.stepSub {
  font-size: 13px;
  color: var(--color-text-muted);
  line-height: 1.6;
}

/* ── Form fields ────────────────────────────────────────────── */
.fields { display: flex; flex-direction: column; gap: var(--sp-5); }

.label {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: var(--font-mono);
}

.input, .select, .textarea {
  width: 100%;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--sp-3) var(--sp-4);
  font-size: 14px;
  color: var(--color-text);
  font-family: var(--font-sans, inherit);
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.input::placeholder, .textarea::placeholder { color: var(--color-text-faint); }
.input:focus, .select:focus, .textarea:focus { border-color: rgba(56, 189, 248, 0.4); }

.textarea { resize: vertical; line-height: 1.6; }

.select { cursor: pointer; appearance: auto; }

/* ── Entity / KPI chip grid ─────────────────────────────────── */
.checkGrid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
}

.chip {
  font-size: 13px;
  padding: var(--sp-2) var(--sp-3);
  border-radius: 999px;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  white-space: nowrap;
}

.chip:hover { border-color: var(--color-border-med); color: var(--color-text); }

.chipActive {
  background: rgba(56, 189, 248, 0.12);
  border-color: rgba(56, 189, 248, 0.35);
  color: var(--color-sky);
}

.chipCheck { font-size: 11px; }

/* ── KPI section ────────────────────────────────────────────── */
.kpiSection { display: flex; flex-direction: column; gap: var(--sp-4); }

.kpiSuggestLabel {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.kpiAdd {
  display: flex;
  gap: var(--sp-2);
  align-items: center;
}

.kpiAdd .input { margin: 0; }

.btnSmall {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  padding: var(--sp-2) var(--sp-4);
  border-radius: var(--radius-md);
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.25);
  color: var(--color-sky);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s;
}

.btnSmall:hover { background: rgba(56, 189, 248, 0.18); }

.kpiPills { display: flex; flex-wrap: wrap; gap: var(--sp-2); }

.kpiPill {
  font-family: var(--font-mono);
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(13, 148, 136, 0.12);
  border: 1px solid rgba(13, 148, 136, 0.3);
  color: #2dd4bf;
}

/* ── Context preview (step 4) ───────────────────────────────── */
.contextCard {
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--sp-5);
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.contextLabel {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--color-sky);
}

.contextArea {
  width: 100%;
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--sp-3) var(--sp-4);
  font-size: 14px;
  color: var(--color-text);
  line-height: 1.7;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.contextArea:focus { border-color: rgba(56, 189, 248, 0.4); }

.contextHint {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-text-faint);
  text-align: right;
}

/* ── Actions bar ────────────────────────────────────────────── */
.actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-3);
  padding-top: var(--sp-2);
}

.btnPrimary {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 700;
  padding: var(--sp-3) var(--sp-6);
  border-radius: var(--radius-md);
  background: rgba(56, 189, 248, 0.15);
  border: 1px solid rgba(56, 189, 248, 0.35);
  color: var(--color-sky);
  cursor: pointer;
  transition: background 0.15s;
}

.btnPrimary:hover:not(:disabled) { background: rgba(56, 189, 248, 0.25); }
.btnPrimary:disabled { opacity: 0.4; cursor: not-allowed; }

.btnGhost {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 600;
  padding: var(--sp-3) var(--sp-5);
  border-radius: var(--radius-md);
  background: transparent;
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.btnGhost:hover { border-color: var(--color-border-med); color: var(--color-text); }
